import later from '@breejs/later'
import { addDays, format, isToday, isValid, parseISO } from 'date-fns'
import { fromUTC, ordinalSuffix } from 'src/lib/utils/helpers'
import { Report, ReportRun } from './types'

/**
 * Human label for a report type, sourced from the locale file (`report.type.<type>`).
 * Falls back to a humanized version of the raw type when no translation exists.
 */
export const formatReportType = (type: string | undefined, t: (key: string) => string): string => {
  if (!type) return '-'
  const key = `report.type.${type}`
  const label = t(key)
  // i18next returns the key itself when a translation is missing.
  return label === key ? type.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase()) : label
}

const toDate = (value?: string): Date | null => {
  if (!value) return null
  const parsed = parseISO(value)
  return isValid(parsed) ? parsed : null
}

const formatHour = (hour: number): string => {
  if (hour === 0) return '12AM'
  if (hour < 12) return `${hour}AM`
  if (hour === 12) return '12PM'
  return `${hour - 12}PM`
}

const SHORT_DAYS = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN']
const formatRecurringSchedule = (frequency?: string): string => {
  if (!frequency) return 'Recurring'
  try {
    const cron = frequency.replace(/\?/g, '*')
    const schedule = later.parse.cron(cron)
    const slot = schedule?.schedules?.[0]
    if (!slot || slot.h === undefined) return 'Recurring'
    const time = formatHour(slot.h[0])

    if (slot.d?.[0] !== undefined) {
      const dayPart = cron.split(' ')[4]
      const shortDay = SHORT_DAYS.find(day => dayPart?.toUpperCase().includes(day)) ?? SHORT_DAYS[slot.d[0] - 2] ?? 'MON'
      const label = shortDay.charAt(0) + shortDay.slice(1).toLowerCase()
      return `Runs Weekly ${label}, ${time}`
    }
    if (slot.D?.[0] !== undefined) {
      const day = slot.D[0]
      return `Runs Monthly ${day}${ordinalSuffix(day)}, ${time}`
    }
    return `Runs Daily ${time}`
  } catch {
    return 'Recurring'
  }
}

const formatDateLabel = (date: Date): string => format(date, 'MMM d')

/** Compact schedule text for the "Schedule" column, e.g. "Runs Daily 12AM" / "Covers Jul 1 – Jul 31". */
export const formatReportSchedule = (report: Report): string => {
  if (report.frequencyType === 'RECURRING') {
    const firstRun = fromUTC(report.runStartAt)
    if (report.runs.length === 0 && firstRun && firstRun.getTime() > Date.now()) {
      return `First run ${formatDateLabel(firstRun)}`
    }
    return formatRecurringSchedule(report.frequency)
  }

  const start = fromUTC(report.startDate) ?? fromUTC(report.runs[0]?.startDate)
  const endBoundary = fromUTC(report.endDate) ?? fromUTC(report.runs[0]?.endDate)
  const end = endBoundary ? addDays(endBoundary, -1) : null
  if (start && end) {
    return `Covers ${formatDateLabel(start)} – ${formatDateLabel(end)}`
  }
  return 'One time'
}

/** The recurring cadence label (e.g. "Runs Daily 12AM") — used for the "First run" info tooltip. */
export const getRecurringCadenceLabel = (report: Report): string =>
  report.frequencyType === 'RECURRING' ? formatRecurringSchedule(report.frequency) : ''

export const formatLastGenerated = (report: Report): string => {
  const run = report.runs[0]
  if (run?.createdAt) {
    const date = toDate(run.createdAt)
    if (date) return isToday(date) ? 'Today' : format(date, 'MMM dd')
  }
  return 'No runs'
}

/** Full "Generated on …" label for the Last-generated tooltip (null when there are no runs). */
export const formatLastGeneratedTooltip = (report: Report): string | null => {
  const date = report.runs[0]?.createdAt ? toDate(report.runs[0].createdAt) : null
  return date ? `Generated on ${format(date, 'MMM dd, yyyy')}` : null
}

export type ReportRowStatus = 'ready' | 'generating' | 'failed' | 'paused' | 'no_runs'

export interface ReportRowState {
  status: ReportRowStatus
  /** Inline label shown beside the report name (failure reason / paused). */
  inlineLabel?: string
  latestRun?: ReportRun
}

export const getReportRowState = (report: Report): ReportRowState => {
  const latestRun = report.runs[0]

  if (report.status === 'paused') {
    return { status: 'paused', inlineLabel: 'Paused', latestRun }
  }
  if (!latestRun) {
    return { status: 'no_runs', latestRun }
  }
  switch (latestRun.status) {
    case 'PROCESSING':
    case 'SCHEDULED':
      return { status: 'generating', latestRun }
    case 'FAILED':
      // TODO(backend): expose granular failure reason (generation/delivery/export). Defaulting to generation.
      return { status: 'failed', inlineLabel: 'Generation failed', latestRun }
    default:
      return { status: 'ready', latestRun }
  }
}
